<script setup>
import {toRefs, computed} from 'vue'

//Emits and props
defineEmits([
  'click',
])
const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  rotate: {
    type: Number,
    default: 0,
    validator: (value) => value >= -360 && value <= 360,
  },
  isInline: {
    type: Boolean,
    default: false,
  },
  isOutlined: {
    type: Boolean,
    default: false,
  },
})

//Extract props and determine transformation
const {rotate} = toRefs(props)
const transform = computed(() => rotate.value ? `rotate(${rotate.value}deg)` : '')
</script>

<template>
  <span
    :class="{
      [$style.icon]: true,
      [$style[size]]: !!size,
      [$style[color]]: !!color,
      [$style.inline]: isInline,
      [$style.outlined]: isOutlined,
    }"
    role="presentation"
    :style="{transform}"
    v-bind="$attrs"
    @click="$emit('click', $event)"
  >
    {{ icon }}
  </span>
</template>

<style module lang="scss">
.icon {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px; /* "m" */
  vertical-align: top;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  text-decoration: none !important;
  transition: all var(--transition-cubic-fast);
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
  font-variation-settings:
    'FILL' 1,
    'wght' 400,
    'GRAD' 0,
    'opsz' 24;

  /* Outlined */
  &.outlined {
    font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
  }

  /* Colors */
  &.primary {
    color: var(--bg-primary-element);
  }
  &.success {
    color: var(--color-status-success);
  }
  &.warning {
    color: var(--color-status-warning);
  }
  &.danger {
    color: var(--color-status-danger);
  }
  &.info {
    color: var(--color-status-info);
  }
  &.muted {
    color: var(--color-text-muted);
  }

  /* Sizing */
  &.xs {
    font-size: 16px;
    &.inline {
      transform: translateY(3px);
    }
  }
  &.s {
    font-size: 20px;
  }
  &.m {
    font-size: 24px;
  }
  &.l {
    font-size: 32px;
  }
  &.xl {
    font-size: 48px;
  }
  &.xxl {
    font-size: 64px;
  }
  &.xxxl {
    font-size: 96px;
  }
}
</style>
